// ============================================================================
// BudgetContext.jsx - Enhanced Budget Context with Service Layer
// Location: src/context/BudgetContext.jsx
// ============================================================================

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import BudgetService from '../services/BudgetService';
import { toast } from 'react-toastify';
// 🟢 NEW: Import Auth Context
import { useAuth } from './AuthContext';

const BudgetContext = createContext();

export const useBudgets = () => {
  const context = useContext(BudgetContext);
  if (!context) {
    throw new Error('useBudgets must be used within BudgetProvider');
  }
  return context;
};

export const BudgetProvider = ({ children }) => {
  const [budgets, setBudgets] = useState([]);
  const [budgetVsActual, setBudgetVsActual] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 🟢 NEW: Get the current user
  const { user } = useAuth();

  // Fetch all budgets (for BudgetPage)
  const fetchBudgets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await BudgetService.getAllBudgets();
      setBudgets(data || []);
      return data || [];
    } catch (err) {
      setError(err.message);
      setBudgets([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch budget vs actual analysis (for BudgetVsActual page)
  const fetchBudgetVsActual = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await BudgetService.getBudgetVsActual();
      setBudgetVsActual(data || []);
      return data || [];
    } catch (err) {
      setError(err.message);
      setBudgetVsActual([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Get budget by budget ID
  const getBudgetById = useCallback(async (budgetId) => {
    if (!budgetId) return null;
    try {
      const data = await BudgetService.getBudgetById(budgetId);
      return data;
    } catch (err) {
      console.warn('Failed to fetch budget by id:', err);
      return null;
    }
  }, []);

  // Get budget for specific project
  const getBudgetByProject = useCallback(async (projectId) => {
    setLoading(true);
    try {
      const data = await BudgetService.getBudgetByProject(projectId);
      return data;
    } catch (err) {
      toast.error('Failed to fetch project budget');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Add or update budget (upsert pattern)
  const addBudget = useCallback(async (budgetData) => {
    setLoading(true);
    try {
      // Check if budget exists for this project
      const existing = budgets.find(b => 
        String(b.projectId) === String(budgetData.projectId)
      );

      let result;
      if (existing) {
        result = await BudgetService.updateBudget(existing.id, budgetData);
        setBudgets(prev => prev.map(b => 
          b.id === existing.id ? result : b
        ));
        toast.success('Budget updated successfully');
      } else {
        result = await BudgetService.createBudget(budgetData);
        setBudgets(prev => [...prev, result]);
        toast.success('Budget created successfully');
      }
      
      return result;
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save budget');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [budgets]);

  // Update budget
  const updateBudget = useCallback(async (budgetId, budgetData) => {
    setLoading(true);
    try {
      const result = await BudgetService.updateBudget(budgetId, budgetData);
      setBudgets(prev => prev.map(b => 
        b.id === budgetId ? result : b
      ));
      toast.success('Budget updated successfully');
      return result;
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update budget');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Delete budget
  const deleteBudget = useCallback(async (budgetId) => {
    setLoading(true);
    try {
      await BudgetService.deleteBudget(budgetId);
      setBudgets(prev => prev.filter(b => b.id !== budgetId));
      toast.success('Budget deleted successfully');
      return true;
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete budget');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user || !user.id) {
      setBudgets([]); // Clear data on logout
    }
  }, [user]);

  const value = {
    // State
    budgets,
    budgetVsActual,
    loading,
    error,
    
    // Budget operations
    fetchBudgets,
    fetchBudgetVsActual,
    getBudgetById,
    getBudgetByProject,
    addBudget,
    updateBudget,
    deleteBudget,
  };

  return (
    <BudgetContext.Provider value={value}>
      {children}
    </BudgetContext.Provider>
  );
};